import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { COUNTRIES, DEFAULT_COUNTRY, findCountryByDialCode, flagUrl } from '../../../utils/countries';
import './PhoneInput.scss';

const PhoneInput = ({
  label = 'Telefone',
  id,
  className = '',
  name = 'customerPhone',
  value = '',
  countryCode = DEFAULT_COUNTRY.dialCode,
  onChange,
  onCountryChange,
  errorMessage = '',
  isValid = false,
  disabled = false,
  placeholder = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  const selectedCountry = useMemo(
    () => findCountryByDialCode(countryCode),
    [countryCode]
  );

  const filteredCountries = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return COUNTRIES;
    return COUNTRIES.filter(
      (country) =>
        country.name.toLowerCase().includes(query) ||
        country.dialCode.includes(query) ||
        country.code.toLowerCase().includes(query)
    );
  }, [search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
    if (!isOpen) {
      setSearch('');
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  const handleSelectCountry = (country) => {
    if (onCountryChange) {
      onCountryChange(country.dialCode, country);
    }
    setIsOpen(false);
  };

  const handlePhoneChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, '');
    if (onChange) {
      onChange({ target: { name, value: digitsOnly } });
    }
  };

  return (
    <div className={`phone-input-field ${className}`} ref={wrapperRef}>
      {label && <div className="title">{label}</div>}
      <div
        className={`phone-input-group ${errorMessage ? 'error' : ''} ${isValid ? 'valid' : ''} ${disabled ? 'disabled' : ''}`}
      >
        <button
          type="button"
          className={`country-trigger ${isOpen ? 'open' : ''}`}
          onClick={handleToggle}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label="Selecionar país"
        >
          <img
            className="flag"
            src={flagUrl(selectedCountry.code)}
            alt={`Bandeira ${selectedCountry.name}`}
            loading="lazy"
          />
          <span className="dial-code">{selectedCountry.dialCode}</span>
          <span className={`chevron ${isOpen ? 'open' : ''}`} aria-hidden="true" />
        </button>

        <input
          id={id}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          disabled={disabled}
          className="phone-input"
          name={name}
          value={value}
          placeholder={placeholder || 'Apenas números'}
          onChange={handlePhoneChange}
        />
      </div>

      {isOpen && (
        <div className="country-dropdown" role="listbox">
          <div className="country-search">
            <input
              ref={searchRef}
              type="text"
              placeholder="Buscar país ou código"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <ul className="country-list">
            {filteredCountries.length === 0 && (
              <li className="country-empty">Nenhum país encontrado</li>
            )}
            {filteredCountries.map((country) => {
              const isSelected = country.code === selectedCountry.code;
              return (
                <li
                  key={country.code}
                  role="option"
                  aria-selected={isSelected}
                  className={`country-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleSelectCountry(country)}
                >
                  <img
                    className="flag"
                    src={flagUrl(country.code)}
                    alt={`Bandeira ${country.name}`}
                    loading="lazy"
                  />
                  <span className="country-name">{country.name}</span>
                  <span className="country-dial">{country.dialCode}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {errorMessage && <div className="input-error-msg">{errorMessage}</div>}
    </div>
  );
};

PhoneInput.propTypes = {
  label: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  countryCode: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onCountryChange: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  isValid: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default PhoneInput;
